// Scoreboard tab: league-wide highlights, not a second standings list.
// Standings (season rank, playoff bracket) live on the League tab's
// "Standings" section (MyLeague.js). This tab answers a different question:
// what happened this week, and what's the best anyone has ever done.
//
// Two round trips total, both against weekly_lineups/games, and cached apart
// because only one of them depends on the selected week:
//   1. weekly_lineups for the whole league, every week, unfiltered. Small
//      (weeks x members), and reused for both the current-week hero/carousel
//      AND the season-high scan, instead of querying twice. Cached under a
//      key with no week in it: this answer is the same whichever week is
//      showing, and keying it per week meant every tap of the week arrow
//      refetched the entire season's lineups to redraw one of them.
//   2. games for the selected week, for live/final/spread display per team.
//      A past week's games are final, so those are cached to disk.
//   3. team_season_stats for every FBS team, which is where fantasy points
//      per team per week live. The NCAA-team highlights used to read these
//      out of useTeams(), but that map is loaded once at boot and espn.js now
//      scores a game while it runs, so on a gameday it was showing the
//      standings as they were when the app opened.
// All three are refetched on a 60 second poll while a game is live.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Armchair, ChevronLeft, ChevronRight, Crown, Trophy, Sparkles, Zap } from "lucide-react";
import { useLeague } from "../context/LeagueContext";
import { getCurrentWeekNumber } from "../utils/leagueUtils";
import { useTeams } from "../hooks/useTeams";
import { supabase } from "../supabase/supabase";
import LoadingScreen from "../components/LoadingScreen";
import { getCached, setCached, ttlForGames, markLive, LIVE_TTL_MS } from "../utils/queryCache";
import { TOTAL_LINEUP_WEEKS, SEASON_YEAR } from "../utils/season";
import { getGameDisplayInfo, getGameSpreadDisplay,
  isSuperDog, buildGamesBySlug, anyGameSoon, POLL_MS } from "../utils/gameStatus";
import { anySuperDog } from "../utils/superDog";
import SuperDogLegend from "../components/SuperDogLegend";
import { fetchWeekGames } from "../utils/weekGames";
import ReadOnlyTeamRow, { OUTCOME_COLORS } from "../components/scoreboard/ReadOnlyTeamRow";
import TeamDrawer from "../components/league/TeamDrawer";
import SwipeDeck from "../components/scoreboard/SwipeDeck";
import { usePageTour } from "../hooks/usePageTour";
import ShareButton from "../components/ShareButton";
import TourAnchor from "../tours/TourAnchor";
import { useFreezeEvents } from "../hooks/useFreezeEvents";
import { buildLineupShareText, weekRankOf } from "../utils/lineupShare";

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-1">
      <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: color }} />
      {label}
    </div>
  );
}

// One row of the "Week N stats" card. Always renders, even when `entry` is
// null, so the five stats read as a fixed checklist for the week rather than
// a list that grows items in as the week plays out — the whole point of
// keeping every slot visible pre-kickoff is being able to see what's coming,
// not just what already exists.
// Every "Week N stats" row picks a single winner from a sort, so a genuine
// tie was previously invisible: whichever row happened to sort first quietly
// took sole credit. This turns the tied team names into the label itself
// rather than picking one, so the row stays honest without growing into a
// list: two names fit on the line, three or more collapse to a count.
function tiedNameLabel(names) {
  if (names.length <= 1) return names[0] || "";
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names[0]} +${names.length - 1} more`;
}

function StatRow({ icon: Icon, iconColor, label, entry, emptyMessage, note, borderTop }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3" style={borderTop ? { borderTop: "1px solid var(--color-border)" } : undefined}>
      <Icon size={16} className="flex-shrink-0" style={{ color: iconColor }} />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--color-text-muted)" }}>{label}</div>
        {note && (
          <div className="text-[11px]" style={{ color: "var(--color-text-faint)" }}>{note}</div>
        )}
        {entry ? (
          <>
            <div className="text-sm font-bold truncate" style={{ color: "var(--color-text-strong)" }}>{entry.teamName}</div>
            {entry.subtitle && (
              <div className="text-xs truncate" style={{ color: "var(--color-text-muted)" }}>{entry.subtitle}</div>
            )}
          </>
        ) : (
          <div className="text-sm" style={{ color: "var(--color-text-muted)" }}>{emptyMessage}</div>
        )}
      </div>
      {entry && (
        <div className="text-lg font-bold flex-shrink-0" style={{ color: entry.valueColor || "var(--color-brand)" }}>
          {entry.value}
        </div>
      )}
    </div>
  );
}

function TeamRowSection({ label, slugs, allTeams, gamesBySlug, weeklyPointsBySlug, manager, week, freezeEvents, onOpenTeam }) {
  if (slugs.length === 0) return null;
  return (
    <div>
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--color-text-muted)" }}>{label}</span>
        <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--color-text-muted)" }}>Score</span>
      </div>
      {slugs.map((slug, i) => {
        const gameInfo = gamesBySlug[slug];
        return (
          <ReadOnlyTeamRow
            key={slug}
            slug={slug}
            index={i}
            team={allTeams?.[slug]}
            gameInfo={gameInfo}
            weeklyPoints={weeklyPointsBySlug?.[slug]}
            gameDisplayInfo={getGameDisplayInfo(gameInfo, slug, allTeams)}
            gameSpread={getGameSpreadDisplay(gameInfo, slug)}
            isSuperDog={isSuperDog(gameInfo, slug)}
            isCaptain={manager.captain === slug}
            isTripPlay={manager.tripPlayTeam === slug}
            frozenPoints={(manager.frozenTeams || []).includes(slug) ? manager.teamPoints?.[slug] : undefined}
            freezeDetail={freezeEvents?.get(`${manager.memberId}:${slug}`)}
            week={week}
            onOpenTeam={onOpenTeam}
          />
        );
      })}
    </div>
  );
}

function ManagerCard({ manager, allTeams, gamesBySlug, weeklyPointsBySlug, week, weekTotals, freezeEvents, onOpenTeam }) {
  // Built on tap, from what the card is showing at that moment.
  const getShareText = () => buildLineupShareText({
    teamName: manager.teamName,
    managerName: manager.firstName,
    week,
    points: manager.weeklyPoints,
    starters: manager.starters,
    captain: manager.captain,
    tripPlayTeam: manager.tripPlayTeam,
    frozenTeams: manager.frozenTeams,
    teamPoints: manager.teamPoints,
    allTeams,
    gamesBySlug,
    weeklyPointsBySlug,
    weekRank: weekRankOf(manager.memberId, weekTotals),
    managerCount: weekTotals.length,
  });

  return (
    <div
      className="w-full bg-surface-card rounded-2xl border shadow-sm overflow-hidden"
      style={{ borderColor: manager.isMe ? "var(--color-brand)" : "var(--color-border)" }}
    >
      <div className="flex items-center justify-between px-3 py-2.5" style={{ borderBottom: "1px solid var(--color-border)" }}>
        <div className="min-w-0">
          <div className="text-sm font-bold truncate" style={{ color: "var(--color-text-strong)" }}>{manager.teamName}</div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs truncate" style={{ color: "var(--color-text-muted)" }}>{manager.firstName}</span>
            {/* Same pill as the League tab's standings row, so a manager
                spotted here and one spotted there read as the same fact
                rather than two different-looking indicators for it. The
                card header, not the team row below, is where a reader's eye
                lands first on this tab. */}
            {manager.tripPlayTeam && (
              <span
                className="flex items-center gap-0.5 flex-shrink-0 rounded-full"
                style={{ backgroundColor: "var(--color-tripplay)", padding: "3px 6px" }}
                title="5x Play in play this week"
              >
                <Zap size={10} color="var(--color-bg-card)" fill="var(--color-bg-card)" />
                <span className="text-[10px] font-bold" style={{ color: "var(--color-bg-card)" }}>5X IN PLAY</span>
              </span>
            )}
          </div>
        </div>
        {/* Labelled, since a bare number here read as either the week or the
            season. Same WK and TOTAL words as the League tab's standings
            columns. TOTAL is the season so far, not as of the week shown. */}
        <div className="flex items-start gap-3 flex-shrink-0 text-right">
          <div>
            <div className="text-lg font-bold leading-tight" style={{ color: "var(--color-brand)" }}>{manager.weeklyPoints}</div>
            <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--color-text-muted)" }}>Wk</div>
          </div>
          <div>
            <div className="text-lg font-bold leading-tight" style={{ color: "var(--color-text-strong)" }}>{manager.seasonPoints}</div>
            <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--color-text-muted)" }}>Total</div>
          </div>
          {/* Any manager's card, the viewer's own included, since this deck
              is one card per manager. The negative margin lets the 44px
              target overhang the header's padding instead of making the
              header taller. */}
          <div style={{ margin: "-6px -8px -6px -6px" }}>
            <ShareButton getText={getShareText} label={`Share ${manager.teamName}'s lineup`} />
          </div>
        </div>
      </div>
      <TeamRowSection label="Starters" slugs={manager.starters} allTeams={allTeams} gamesBySlug={gamesBySlug} weeklyPointsBySlug={weeklyPointsBySlug} manager={manager} week={week} freezeEvents={freezeEvents} onOpenTeam={onOpenTeam} />
      <TeamRowSection label="Bench" slugs={manager.bench} allTeams={allTeams} gamesBySlug={gamesBySlug} weeklyPointsBySlug={weeklyPointsBySlug} manager={manager} week={week} freezeEvents={freezeEvents} onOpenTeam={onOpenTeam} />
    </div>
  );
}

function ScoreboardTab() {
  const { leagueId } = useParams();
  const [searchParams] = useSearchParams();
  // Deep link from the League standings row: jump straight to one manager's
  // card instead of making the visitor swipe to find it.
  const deepLinkMemberId = searchParams.get("member");
  const { members: ctxMembers, currentWeek: leagueWeek, currentMemberId, isDemo } = useLeague();
  usePageTour("scoreboard", { demo: isDemo, nextLabel: "Next: the league", nextTo: `/${leagueId}/league` });
  const { teams: allTeams } = useTeams();
  const [week, setWeek] = useState(null);
  const [allLineups, setAllLineups] = useState([]);
  const [gamesBySlug, setGamesBySlug] = useState({});
  // Fantasy points per team slug, refetched rather than read from the boot
  // time AuthContext map. See the load effect below.
  const [weeklyPointsBySlug, setWeeklyPointsBySlug] = useState({});
  // Whether this week is worth polling: something is running, or a kickoff is
  // close enough that one is about to be.
  const [isGameday, setIsGameday] = useState(false);
  const [loading, setLoading] = useState(true);
  const deckRef = useRef(null);
  // Which squad the deck is showing, mirrored here only so the header can
  // name it. The deck owns the real index: a drag in flight has no committed
  // index yet, so the parent cannot be the one holding it.
  const [deckIndex, setDeckIndex] = useState(0);
  // Which manager the deck is on, as a member id rather than a position.
  // Stepping to another week rebuilds carouselRows (different points, so a
  // different order) and, when that week is not cached, unmounts the deck
  // behind the loading screen. Either way an index means a different manager
  // on the other side, and the deck used to come back on card 0, which is
  // always "you": a visitor reading someone else's season had to re-find them
  // after every arrow press. A ref, not state: it only ever feeds the two
  // effects below and the deck's initial index, and re-rendering on it would
  // do nothing but churn.
  const viewedMemberId = useRef(null);
  // The manager pill strip above the deck. Its active pill is kept in view as
  // the deck moves, whether by swipe, dot or pill.
  const pillStripRef = useRef(null);
  // The team whose sheet is open, same as the Lineup tab. Every row on this
  // page names a team, and the question a reader has about an unfamiliar one
  // is the same question in both places.
  const [drawerTeam, setDrawerTeam] = useState(null);

  // League tab's standings rows link here with a member deep link, and that
  // list can be scrolled well past the fold. Without this the tab opened
  // wherever the browser's default scroll-position carryover left it, not at
  // its own top, even though the deck below correctly still jumps to that
  // member's card via initialDeckIndex.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Nothing past the current week has lineups or scores, so neither the
  // arrows nor a ?week= deep link go beyond it.
  const lastViewableWeek = Math.min(TOTAL_LINEUP_WEEKS, getCurrentWeekNumber(leagueWeek) || 1);

  useEffect(() => {
    if (week != null) return;
    // The League page's deep link carries its own week for a historical
    // standings row; otherwise default to whatever week the league is on.
    const paramWeek = Number(searchParams.get("week"));
    if (paramWeek >= 1 && paramWeek <= lastViewableWeek) { setWeek(paramWeek); return; }
    if (leagueWeek) setWeek(leagueWeek);
  }, [leagueWeek, week, searchParams, lastViewableWeek]);

  useEffect(() => {
    let cancelled = false;
    // `quiet` is the live poll: it must not raise the loading screen, and it
    // must not read the lineup cache, since the score it holds is exactly the
    // thing that has moved.
    async function load(quiet) {
      if (!leagueId || !week) return;

      // One week's lineups, keyed per week. This used to fetch the whole
      // season's rows under one key so the week arrow could redraw from cache,
      // but the page only ever shows one week, and on gameday this request
      // repeats every minute: by week 14 that is fourteen weeks of rows
      // downloaded per tick to render one. Stepping to another week now costs
      // a single week's rows, once, and is cached from then on.
      // Games come from the shared per-week cache, which the Teams tab reads
      // from too.
      const lineupsKey = `lineups:${leagueId}:week:${week}`;
      const cachedLineups = quiet ? null : getCached(lineupsKey);

      if (!quiet) setLoading(true);
      const [lineupRows, gamesData, { data: statRows }] = await Promise.all([
        cachedLineups
          ? Promise.resolve(cachedLineups)
          : supabase
            .from("weekly_lineups")
            .select("member_id, week, starters, bench, captain, trip_play_team, frozen_teams, team_points, points")
            .eq("league_id", leagueId)
            .eq("week", week)
            .then(({ data }) => data || []),
        fetchWeekGames(week, { isFinished: week < getCurrentWeekNumber(leagueWeek) }),
        // Read fresh rather than from the AuthContext teams map, which is
        // loaded once at boot. espn.js scores a game while it runs, so that
        // map goes stale inside a quarter.
        supabase
          .from("team_season_stats")
          .select("team_id, weekly_points")
          .eq("season_year", SEASON_YEAR),
      ]);
      if (cancelled) return;
      setAllLineups(lineupRows);
      setGamesBySlug(buildGamesBySlug(gamesData));

      const points = {};
      for (const row of statRows || []) points[row.team_id] = row.weekly_points || {};
      setWeeklyPointsBySlug(points);

      setIsGameday(anyGameSoon(gamesData));
      if (!quiet) setLoading(false);

      if (!cachedLineups) {
        // Never persisted: weekly_lineups.points is rewritten by score
        // ingestion, and a manager's own edits land here too.
        const ttl = ttlForGames(gamesData);
        setCached(lineupsKey, lineupRows, ttl);
        if (ttl === LIVE_TTL_MS) markLive(lineupsKey);
      }
    }
    load();

    const tick = () => { if (document.visibilityState === "visible") load(true); };

    // Coming back to the app always refetches, whatever the week.
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);

    // The interval is gameday only, matching the Lineup tab. espn.js writes on
    // a 2 minute cron, so 60 seconds keeps the page current without doubling
    // up on its cadence.
    const id = isGameday ? setInterval(tick, POLL_MS) : null;

    return () => {
      cancelled = true;
      if (id) clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
    };
  }, [leagueId, week, leagueWeek, isGameday]);

  const weekRows = useMemo(() => allLineups
    .filter((row) => row.week === week)
    .map((row) => {
      const member = ctxMembers.find((m) => m.id === row.member_id);
      return {
        memberId: row.member_id,
        teamName: member?.team_name || "Unnamed team",
        firstName: member?.first_name || "",
        seasonPoints: member?.points ?? 0,
        lastName: member?.last_name || "",
        isMe: row.member_id === currentMemberId,
        weeklyPoints: row.points ?? 0,
        starters: (row.starters || []).filter(Boolean),
        bench: (row.bench || []).filter(Boolean),
        captain: row.captain,
        tripPlayTeam: row.trip_play_team,
        frozenTeams: row.frozen_teams || [],
        teamPoints: row.team_points || {},
      };
    })
    .sort((a, b) => b.weeklyPoints - a.weeklyPoints), [allLineups, week, ctxMembers, currentMemberId]);
  // "You" always sorts first in the carousel regardless of rank, since this
  // isn't a leaderboard, it's a browse-everyone view and your own card is
  // the one worth not scrolling to find.
  const carouselRows = useMemo(
    () => [...weekRows].sort((a, b) => (b.isMe ? 1 : 0) - (a.isMe ? 1 : 0)),
    [weekRows]
  );
  // The snapshot each of this week's freezes was written with, for the panel
  // under a frozen row. Only fetched when something is frozen.
  const freezeEvents = useFreezeEvents({
    leagueId, week, enabled: weekRows.some((r) => r.frozenTeams.length > 0),
  });
  // Every manager's total for the week, which a shared lineup ranks against.
  const weekTotals = useMemo(
    () => weekRows.map((r) => ({ memberId: r.memberId, points: r.weeklyPoints })),
    [weekRows]
  );

  // Handed to SwipeDeck as its initial index, not applied via an imperative
  // jumpTo() after mount: a jumpTo() called from this component's own mount
  // effect races SwipeDeck's passive-effect setup for the same commit and
  // silently loses the update. Only the value at SwipeDeck's own mount reads
  // this; a later poll reordering the carousel does not reopen it.
  // Pill labels: first name, plus last initial where two managers share one.
  const pillLabels = useMemo(() => {
    const counts = {};
    carouselRows.forEach((r) => {
      const key = r.firstName.trim().toLowerCase();
      if (key) counts[key] = (counts[key] || 0) + 1;
    });
    return carouselRows.map((r) => {
      const first = r.firstName.trim();
      if (!first) return r.teamName;
      const initial = r.lastName.trim().charAt(0).toUpperCase();
      return counts[first.toLowerCase()] > 1 && initial ? `${first} ${initial}.` : first;
    });
  }, [carouselRows]);

  // The manager being read wins over the ?member= deep link, which only
  // matters on the first mount: after that the visitor has moved the deck
  // themselves, or stepped weeks with someone open.
  const initialDeckIndex = useMemo(() => {
    const target = viewedMemberId.current || deepLinkMemberId;
    if (!target) return 0;
    const idx = carouselRows.findIndex((r) => r.memberId === target);
    return idx >= 0 ? idx : 0;
  }, [deepLinkMemberId, carouselRows]);

  // A swipe, a pill or an arrow: whatever the deck lands on is the manager to
  // hold on to.
  useEffect(() => {
    const id = carouselRows[deckIndex]?.memberId;
    if (id) viewedMemberId.current = id;
  }, [deckIndex, carouselRows]);

  // The deck survived a week change (this week was cached, so there was no
  // loading screen to unmount it) but the rows under it were re-sorted. Put
  // it back on the same manager. A manager with no lineup that week is not in
  // the new rows at all, in which case the deck stays where it is and the
  // effect above records whoever that turned out to be.
  useEffect(() => {
    const target = viewedMemberId.current;
    if (!target) return;
    const idx = carouselRows.findIndex((r) => r.memberId === target);
    if (idx >= 0 && idx !== deckIndex) deckRef.current?.jumpTo(idx);
    // deckIndex is deliberately not a dependency: this runs when the rows
    // change, not every time the deck moves, which is the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselRows]);

  useEffect(() => {
    const strip = pillStripRef.current;
    const pill = strip?.children[deckIndex];
    if (!pill) return;
    // Set scrollLeft rather than scrollIntoView, which can also scroll the
    // page vertically when the strip sits partly off screen.
    const target = pill.offsetLeft - (strip.clientWidth - pill.offsetWidth) / 2;
    strip.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [deckIndex, carouselRows.length, loading]);

  if (loading || week == null) {
    return <LoadingScreen message="Loading scoreboard..." />;
  }

  // Before kickoff every row is on zero, and "top scorer: 0" is not a
  // result, it is a placeholder wearing one. Points can legitimately be
  // negative once games are played, so the test is that something has moved
  // off zero rather than that the leader is above it.
  const anyPointsScored = weekRows.some((r) => r.weeklyPoints !== 0);
  const topManager = anyPointsScored ? weekRows[0] : null;
  const topManagerCaptainTeam = topManager?.captain ? allTeams?.[topManager.captain] : null;
  // weekRows is sorted best-to-worst (see its useMemo above), so the worst
  // manager of the currently-viewed week is just the other end of the same
  // list. Gated on anyPointsScored the same way topManager is: before
  // kickoff every row reads 0, and "worst: 0" is as much a placeholder as
  // "top: 0" would be.
  const bottomManager = anyPointsScored ? weekRows[weekRows.length - 1] : null;

  // Best/worst week entries for the "Week N stats" card. Null pre-kickoff
  // (topManager/bottomManager are both gated on anyPointsScored), which
  // StatRow renders as "No data yet" rather than hiding the row.
  //
  // topManager/bottomManager are just weekRows[0]/weekRows[last], so a tie
  // at either end silently credited whichever row happened to sort first.
  // These filters find everyone actually sharing that value; the manager
  // and captain detail below only applies when there's exactly one of them,
  // since a tie on the total doesn't mean a tie on who the captain was.
  const bestWeekTied = topManager ? weekRows.filter((r) => r.weeklyPoints === topManager.weeklyPoints) : [];
  const worstWeekTied = bottomManager ? weekRows.filter((r) => r.weeklyPoints === bottomManager.weeklyPoints) : [];
  const bestWeekEntry = topManager && {
    teamName: tiedNameLabel(bestWeekTied.map((r) => r.teamName)),
    // Captain detail folded in here from the old "Top scorer" hero banner,
    // which this row made redundant: same manager, same points, just a
    // bigger font. The hero's one piece of info this row didn't already
    // have was the captain, so that's what moves over rather than the row
    // losing it.
    subtitle: bestWeekTied.length > 1
      ? `${bestWeekTied.length}-way tie`
      : [
          topManager.firstName,
          topManagerCaptainTeam
            ? `Captain: ${topManagerCaptainTeam.school}${topManager.tripPlayTeam === topManager.captain ? " (5x Play)" : ""}`
            : null,
        ].filter(Boolean).join(" · "),
    value: topManager.weeklyPoints,
    valueColor: topManager.weeklyPoints < 0 ? "var(--color-danger-text)" : "var(--color-brand)",
  };
  const worstWeekEntry = bottomManager && {
    teamName: tiedNameLabel(worstWeekTied.map((r) => r.teamName)),
    subtitle: worstWeekTied.length > 1 ? `${worstWeekTied.length}-way tie` : bottomManager.firstName,
    value: bottomManager.weeklyPoints,
    valueColor: bottomManager.weeklyPoints < 0 ? "var(--color-danger-text)" : "var(--color-brand)",
  };

  // Top NCAA teams this week and the season-high single NCAA team week.
  //
  // Points come from the map this page refetches, falling back to the boot
  // time teams map for a team the fetch has not answered for. Feeds
  // teamWeekPoints below (captain calls, bench total), not a manager-facing
  // list of its own: the raw NCAA-wide leaderboard this used to also compute
  // was dropped, since it's not something a manager can act on.
  const pointsFor = (slug, team) =>
    weeklyPointsBySlug[slug] || team?.currentSeason?.weeklyPoints || {};

  // Rostered teams across every squad on the page, since the deck swipes
  // through all of them. Only consumer left is the Super Dog legend below the
  // deck; the pre-kickoff spread preview that used to read this too is gone.
  const rosteredSlugs = [...new Set(weekRows.flatMap((r) => [...r.starters, ...r.bench]))];
  const showSuperDogLegend = anySuperDog(rosteredSlugs, gamesBySlug);

  // ── this week's decisions ──────────────────────────────────────────────
  // Both of these grade a choice a manager made rather than reporting a
  // score, which is what makes them worth reading twice. Neither costs a
  // query: the lineup rows are already here, and per-team weekly points come
  // from the same refetched map the rows above use.
  const teamWeekPoints = (slug) => {
    const pts = pointsFor(slug, allTeams?.[slug])[String(week)];
    return typeof pts === "number" ? pts : null;
  };

  // Points a manager left on their own bench. A bench team on a bye or one
  // yet to play contributes nothing, which is correct: it is not a decision
  // that cost anything yet. Kept as every row's total, not just the max, so
  // a tie at the top can be detected below rather than a forEach silently
  // keeping whichever row it reached first.
  const benchTotals = weekRows
    .map((row) => ({ row, points: row.bench.reduce((sum, slug) => sum + (teamWeekPoints(slug) ?? 0), 0) }))
    .filter((e) => e.points > 0);
  const benchLeaderPoints = benchTotals.length > 0 ? Math.max(...benchTotals.map((e) => e.points)) : null;
  const benchLeaderTied = benchTotals.filter((e) => e.points === benchLeaderPoints);

  // What the captain actually scored with the armband's multiplier applied,
  // not the team's raw week: a captain doubles, and doubles a loss as
  // readily as a win, or multiplies fivefold with the 5x Play sitting on it.
  //
  // A frozen captain is judged on the score it was frozen at, from the
  // lineup's team_points (multiplier applied), not on how the game finished.
  // Otherwise a freeze taken to bank a lead reads as that manager's worst call
  // of the week, when the points they actually kept were the lead.
  //
  // The 5x Play is left out, as in the Hall of Fame's captain records: it
  // puts one captain at five times the scale of the rest, and it has its own
  // records.
  const captainCalls = weekRows
    .map((row) => {
      if (!row.captain || row.tripPlayTeam === row.captain) return null;
      const multiplier = 2;
      const frozenTotal = (row.frozenTeams || []).includes(row.captain) ? row.teamPoints?.[row.captain] : undefined;
      if (typeof frozenTotal === "number") {
        return { row, team: allTeams?.[row.captain], base: frozenTotal / multiplier, multiplier, total: frozenTotal, frozen: true };
      }
      const base = teamWeekPoints(row.captain);
      if (base == null) return null;
      return { row, team: allTeams?.[row.captain], base, multiplier, total: base * multiplier, frozen: false };
    })
    .filter(Boolean)
    .sort((a, b) => b.total - a.total);
  const captainCallLine = (c) => {
    const school = c.team?.school || c.row.captain;
    return c.frozen ? `${school}, frozen at ${c.base}` : `${school} scored ${c.base}`;
  };
  const bestCaptain = captainCalls[0] || null;
  // The lowest captain of the week, positive or not. This used to appear only
  // when a captain had cost points, so a week where every captain scored
  // showed "No captains scored yet this week", which read as though nobody
  // had played. Not gated on having 2+ calls: with only one manager played,
  // best and worst are the same manager, and that's correct.
  const worstCaptain = captainCalls.length > 0 ? captainCalls[captainCalls.length - 1] : null;
  // A tie on the multiplied total can still mean different captains and
  // different 5x usage underneath it (e.g. a 6-point captain doubled vs. a
  // 3-point captain on the 5x Play both land on 12 or 15), so the per-captain
  // detail below is dropped on a tie rather than picking one to speak for all.
  const bestCaptainTied = bestCaptain ? captainCalls.filter((c) => c.total === bestCaptain.total) : [];
  const worstCaptainTied = worstCaptain ? captainCalls.filter((c) => c.total === worstCaptain.total) : [];

  // Remaining three "Week N stats" entries, same null-until-decided shape as
  // bestWeekEntry/worstWeekEntry above.
  const bestCaptainEntry = bestCaptain && {
    teamName: tiedNameLabel(bestCaptainTied.map((c) => c.row.teamName)),
    subtitle: bestCaptainTied.length > 1
      ? `${bestCaptainTied.length}-way tie`
      : captainCallLine(bestCaptain),
    // A positive score carries no sign, matching every other number on the
    // card. "Best" still isn't guaranteed positive: in a week where every
    // captain lost points, the least-bad one tops this list.
    value: bestCaptain.total,
    valueColor: bestCaptain.total >= 0 ? "var(--color-success-text)" : "var(--color-danger-text)",
  };
  const worstCaptainEntry = worstCaptain && {
    teamName: tiedNameLabel(worstCaptainTied.map((c) => c.row.teamName)),
    subtitle: worstCaptainTied.length > 1
      ? `${worstCaptainTied.length}-way tie`
      : captainCallLine(worstCaptain),
    value: worstCaptain.total,
    valueColor: worstCaptain.total >= 0 ? "var(--color-success-text)" : "var(--color-danger-text)",
  };
  const benchLeaderEntry = benchLeaderTied.length > 0 && {
    teamName: tiedNameLabel(benchLeaderTied.map((e) => e.row.teamName)),
    subtitle: benchLeaderTied.length > 1 ? `${benchLeaderTied.length}-way tie` : "Points scored by teams left on the bench",
    value: benchLeaderPoints,
    valueColor: "var(--color-brand)",
  };

  // Perfect week: no benched team outscored any started team, and the
  // captain was the highest (or tied-highest) scorer on the roster. Not
  // hardcoded to "the two worst on the bench": bench depth varies by league
  // (it's whatever the draft didn't spend on starters), so the general form
  // is "the whole bench sits at or below the whole starting five."
  //
  // teamWeekPoints returns null for a team that hasn't reported a score yet,
  // which a bye never will. Falling through to 0 there would make an
  // unplayed starter look like a decided zero and could wrongly clear or
  // fail the check, so a bye is resolved to its real, decided zero and
  // anything else left null so the whole row is skipped until its games are.
  const resolvedTeamPoints = (slug) => {
    const raw = teamWeekPoints(slug);
    if (raw != null) return raw;
    return getGameDisplayInfo(gamesBySlug[slug], slug, allTeams)?.type === "bye-week" ? 0 : null;
  };
  // A week behind the league's current one is over: nothing more will be
  // scored in it, so an empty stat is a fact about that week rather than
  // something still to come. leagueWeek can be null while the context loads,
  // and the present tense is the safer wording to show meanwhile.
  const isPastWeek = Boolean(leagueWeek && week && week < leagueWeek);

  const perfectWeekManagers = weekRows.filter((row) => {
    if (!row.captain || row.starters.length === 0) return false;
    const starterPts = row.starters.map(resolvedTeamPoints);
    if (starterPts.some((p) => p == null)) return false;
    const benchPts = row.bench.map(resolvedTeamPoints).filter((p) => p != null);
    const worstStarter = Math.min(...starterPts);
    if (benchPts.length > 0 && Math.max(...benchPts) > worstStarter) return false;
    return resolvedTeamPoints(row.captain) === Math.max(...starterPts);
  });
  const perfectWeekEntry = perfectWeekManagers.length > 0 && {
    teamName: tiedNameLabel(perfectWeekManagers.map((m) => m.teamName)),
    subtitle: perfectWeekManagers.length > 1
      ? `${perfectWeekManagers.length} managers`
      // One line, like every other row on this card. It carried the
      // definition while a separate two-line note restated it above the
      // name, so the note wrapped and this line truncated mid-word.
      : "Top captain, and no bench team beat a starter",
    value: perfectWeekManagers.length,
    valueColor: "var(--color-captain-deep)",
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--color-bg-page)" }}>
      {/* Full bleed below sm, as on the league and lineup tabs. Text blocks
          keep their own px-4 there; the two carousels deliberately do not, so
          a card peeks off the right edge and the row reads as scrollable. */}
      <div className="max-w-4xl mx-auto sm:px-6 py-6 pb-24">
        {/* No "Scoreboard" heading here: the bottom tab already names this
            screen, repeating it just spends vertical space, same call made
            on the Lineup tab. */}

        {/* Everyone's lineups, one squad per screen. This is what the tab
            is for, so the deck gets the full width rather than a row of
            part-visible cards, and it wraps: the swipe past the last
            manager lands on the first. Your own squad is index 0.

            The week switcher is this heading rather than a separate row
            above it. The heading has to name the week anyway, and two
            controls on the page arguing about which week is showing was
            one more than the page needed. Squad navigation is the swipe
            and the dots below, so the only arrows here move weeks.

            Forward stops at the current week: a week that has not started
            has no lineups or scores to show. */}
        <div className="flex items-center justify-between gap-2 mb-2 px-4 sm:px-0">
          <div className="flex items-center gap-1 min-w-0">
            <button
              onClick={() => setWeek((w) => Math.max(1, w - 1))}
              disabled={week <= 1}
              aria-label="Previous week"
              style={{ background: "none", border: "none", padding: 4, color: week <= 1 ? "var(--color-text-faint)" : "var(--color-brand)" }}
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-sm font-bold truncate" style={{ color: "var(--color-text-strong)" }}>
              Week {week} lineups
            </span>
            <button
              onClick={() => setWeek((w) => Math.min(lastViewableWeek, w + 1))}
              disabled={week >= lastViewableWeek}
              aria-label="Next week"
              style={{ background: "none", border: "none", padding: 4, color: week >= lastViewableWeek ? "var(--color-text-faint)" : "var(--color-brand)" }}
            >
              <ChevronRight size={18} />
            </button>
          </div>
          {carouselRows.length > 0 && (
            <span className="text-xs flex-shrink-0" style={{ color: "var(--color-text-muted)" }}>
              {deckIndex + 1} of {carouselRows.length}
            </span>
          )}
        </div>

        {weekRows.length === 0 ? (
          <div className="bg-surface-card sm:rounded-2xl p-8 border-y sm:border border-hairline shadow-sm text-center">
            <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>No lineups set for this week yet.</p>
          </div>
        ) : (
          <>
            {/* One pill per manager, in deck order, so a squad deep in a big
                league is one tap away rather than several swipes. Scrolls
                sideways instead of wrapping, keeping the deck in the same
                place whatever the league size. */}
            {carouselRows.length > 1 && (
              <div
                ref={pillStripRef}
                data-tour="scoreboard-pills"
                className="flex gap-2 overflow-x-auto mb-2 px-4 sm:px-0"
                style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
              >
                {carouselRows.map((row, i) => {
                  const active = i === deckIndex;
                  return (
                    <button
                      key={row.memberId}
                      onClick={() => deckRef.current?.jumpTo(i)}
                      aria-label={`Show ${row.teamName}`}
                      aria-current={active}
                      className="flex-shrink-0 text-xs font-semibold whitespace-nowrap"
                      style={{
                        padding: "6px 12px",
                        borderRadius: 999,
                        border: `1px solid ${active ? "var(--color-brand)" : "var(--color-border)"}`,
                        backgroundColor: active ? "var(--color-brand)" : "var(--color-bg-card)",
                        color: active ? "var(--color-bg-card)" : "var(--color-text-strong)",
                      }}
                    >
                      {pillLabels[i]}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mb-2 px-4 sm:px-0" style={{ position: "relative" }}>
              {/* The manager's heading and first two teams, not the whole
                  card: see TourAnchor. */}
              <TourAnchor name="scoreboard-card" height={190} />
              <SwipeDeck
                ref={deckRef}
                length={carouselRows.length}
                initialIndex={initialDeckIndex}
                onIndexChange={setDeckIndex}
                label="Squad"
                renderAt={(i) => (
                  <ManagerCard
                    manager={carouselRows[i]}
                    allTeams={allTeams}
                    gamesBySlug={gamesBySlug}
                    weeklyPointsBySlug={weeklyPointsBySlug}
                    week={week}
                    weekTotals={weekTotals}
                    freezeEvents={freezeEvents}
                    onOpenTeam={setDrawerTeam}
                  />
                )}
              />
            </div>

            {/* One legend for the deck, not one per card: every squad in the
                carousel uses the same mark, and it would otherwise repeat on
                each swipe. */}
            {showSuperDogLegend && <div data-tour="scoreboard-superdog"><SuperDogLegend className="px-4 sm:px-0 mb-2" /></div>}

            {/* Position dots: with the cards no longer side by side, this is
                the only thing saying how many squads there are to swipe
                through. Tappable, since a nine-team league is a long way to
                swipe back. */}
            {carouselRows.length > 1 && (
              <div className="flex justify-center gap-1.5 mb-3">
                {carouselRows.map((row, i) => (
                  <button
                    key={row.memberId}
                    onClick={() => deckRef.current?.jumpTo(i)}
                    aria-label={`Show ${row.teamName}`}
                    aria-current={i === deckIndex}
                    style={{
                      width: i === deckIndex ? 18 : 6,
                      height: 6,
                      borderRadius: 3,
                      border: "none",
                      padding: 0,
                      transition: "width 200ms ease",
                      backgroundColor: i === deckIndex ? "var(--color-brand)" : "var(--color-text-faint)",
                    }}
                  />
                ))}
              </div>
            )}

            {/* Outcome stripe legend */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-6 px-4 sm:px-0 text-[11px]" style={{ color: "var(--color-text-muted)" }}>
              <Legend color={OUTCOME_COLORS["win-cover"]} label="Won + covered" />
              <Legend color={OUTCOME_COLORS["win-nocover"]} label="Won" />
              <Legend color={OUTCOME_COLORS["loss-cover"]} label="Lost + covered" />
              <Legend color={OUTCOME_COLORS["loss-nocover"]} label="Lost" />
              <Legend color={OUTCOME_COLORS.push} label="Push" />
              <Legend color={OUTCOME_COLORS.upcoming} label="Upcoming / bye" />
            </div>

            {/* Week N stats: every slot always renders, "No data yet" and
                all, rather than the section appearing piece by piece as the
                week plays out. The point of showing an empty slot pre-kickoff
                is seeing what's coming, not just what already exists. */}
            <div className="flex items-center gap-2 mb-2 mt-6 px-4 sm:px-0">
              <span className="text-sm font-bold" style={{ color: "var(--color-text-strong)" }}>Week {week} Stats</span>
            </div>

            {/* A finished week cannot still be waiting for anything, so the
                empty states change tense once the week is behind the league's
                current one. "Nobody's had a perfect week yet" under week 2 in
                November reads as though the week were still being played. */}
            <div className="bg-surface-card sm:rounded-2xl border-y sm:border border-hairline shadow-sm mb-6 overflow-hidden">
              <StatRow icon={Trophy} iconColor="var(--color-captain-deep)" label="Best week" entry={bestWeekEntry} emptyMessage={isPastWeek ? "Nobody scored this week." : "No scores yet this week."} />
              <StatRow icon={Trophy} iconColor="var(--color-text-faint)" label="Worst week" entry={worstWeekEntry} emptyMessage={isPastWeek ? "Nobody scored this week." : "No scores yet this week."} borderTop />
              <StatRow icon={Crown} iconColor="var(--color-captain-deep)" label="Best captain's call" entry={bestCaptainEntry} emptyMessage={isPastWeek ? "No captain scored this week." : "No captains scored yet this week."} borderTop />
              <StatRow icon={Crown} iconColor="var(--color-text-faint)" label="Worst captain's call" entry={worstCaptainEntry} emptyMessage={isPastWeek ? "No captain scored this week." : "No captains scored yet this week."} borderTop />
              <StatRow icon={Armchair} iconColor="var(--color-text-muted)" label="Most left on the bench" entry={benchLeaderEntry} emptyMessage={isPastWeek ? "No bench points to compare this week." : "No bench points to compare yet."} borderTop />
              <StatRow icon={Sparkles} iconColor={perfectWeekManagers.length > 0 ? "var(--color-captain-deep)" : "var(--color-text-faint)"} label="Perfect weeks" entry={perfectWeekEntry} emptyMessage={isPastWeek ? "Nobody had a perfect week this week." : "Nobody's had a perfect week yet."} borderTop />
            </div>
          </>
        )}
      </div>

      <TeamDrawer teamName={drawerTeam} onClose={() => setDrawerTeam(null)} onSelectTeam={setDrawerTeam} />
    </div>
  );
}

export default ScoreboardTab;
